/*
  Manmukh Ayurveda Enterprise Framework
  File: ma-core.js
  Purpose: lightweight runtime primitives for framework sections and snippets.
*/

(function () {
  "use strict";

  var doc = document;
  var root = doc.documentElement;
  var namespace = "ManmukhAyurveda";
  var initialized = new WeakSet();
  var loadedAssets = new Map();
  var activeModal = null;
  var previousFocus = null;
  var scrollLockDepth = 0;
  var uidCounter = 0;

  var selectors = {
    accordion: "[data-ma-accordion]",
    accordionItem: "[data-ma-accordion-item]",
    accordionTrigger: "[data-ma-accordion-trigger]",
    accordionPanel: "[data-ma-accordion-panel]",
    tabs: "[data-ma-tabs]",
    tab: "[data-ma-tab]",
    tabPanel: "[data-ma-tab-panel]",
    modal: "[data-ma-modal]",
    modalOpen: "[data-ma-modal-open]",
    modalClose: "[data-ma-modal-close]",
    slider: "[data-ma-slider]",
    sliderTrack: "[data-ma-slider-track]",
    sliderItem: "[data-ma-slider-item]",
    sliderPrev: "[data-ma-slider-prev]",
    sliderNext: "[data-ma-slider-next]",
    sliderDots: "[data-ma-slider-dots]",
    sliderDot: "[data-ma-slider-dot]",
    form: "[data-ma-form]",
    scrollTo: "[data-ma-scroll-to]",
    trigger: "[data-ma-trigger]"
  };

  var focusableSelector = [
    "a[href]",
    "area[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "iframe",
    "audio[controls]",
    "video[controls]",
    "[contenteditable='true']",
    "[tabindex]:not([tabindex='-1'])"
  ].join(",");

  function ready(callback) {
    if (doc.readyState === "loading") {
      doc.addEventListener("DOMContentLoaded", callback, { once: true });
      return;
    }

    callback();
  }

  function query(selector, context) {
    return (context || doc).querySelector(selector);
  }

  function queryAll(selector, context) {
    return Array.prototype.slice.call((context || doc).querySelectorAll(selector));
  }

  function closest(element, selector) {
    if (!element || element === doc) return null;
    return element.closest(selector);
  }

  function toBoolean(value, fallback) {
    if (value === undefined || value === null || value === "") return Boolean(fallback);
    if (typeof value === "boolean") return value;
    return !/^(false|0|no|off)$/i.test(String(value).trim());
  }

  function toNumber(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function toArray(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return String(value)
      .split(",")
      .map(function (item) {
        return item.trim();
      })
      .filter(Boolean);
  }

  function uid(prefix) {
    uidCounter += 1;
    return (prefix || "ma") + "-" + Date.now().toString(36) + "-" + uidCounter.toString(36);
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === "function") {
      return window.CSS.escape(String(value));
    }

    return String(value).replace(/[^a-zA-Z0-9_-]/g, function (character) {
      return "\\" + character;
    });
  }

  function getSessionStorage() {
    try {
      return window.sessionStorage || null;
    } catch (error) {
      return null;
    }
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function emit(element, name, detail) {
    var event = new CustomEvent("ma:" + name, {
      bubbles: true,
      cancelable: true,
      detail: detail || {}
    });

    element.dispatchEvent(event);
    return event;
  }

  function debounce(callback, wait) {
    var timeout = null;

    return function debounced() {
      var context = this;
      var args = arguments;

      window.clearTimeout(timeout);
      timeout = window.setTimeout(function () {
        callback.apply(context, args);
      }, wait || 120);
    };
  }

  function throttle(callback, wait) {
    var waiting = false;
    var lastArgs = null;
    var lastContext = null;

    function later() {
      if (lastArgs) {
        callback.apply(lastContext, lastArgs);
        lastArgs = null;
        lastContext = null;
        window.setTimeout(later, wait || 120);
        return;
      }

      waiting = false;
    }

    return function throttled() {
      if (waiting) {
        lastArgs = arguments;
        lastContext = this;
        return;
      }

      waiting = true;
      callback.apply(this, arguments);
      window.setTimeout(later, wait || 120);
    };
  }

  function getFocusable(container) {
    return queryAll(focusableSelector, container).filter(function (element) {
      return Boolean(
        element.offsetWidth ||
          element.offsetHeight ||
          element.getClientRects().length
      );
    });
  }

  function lockScroll() {
    scrollLockDepth += 1;
    if (scrollLockDepth === 1) {
      root.classList.add("ma-scroll-lock");
      doc.body.classList.add("ma-scroll-lock");
    }
  }

  function unlockScroll() {
    scrollLockDepth = Math.max(0, scrollLockDepth - 1);
    if (scrollLockDepth === 0) {
      root.classList.remove("ma-scroll-lock");
      doc.body.classList.remove("ma-scroll-lock");
    }
  }

  function normalizePhone(phone) {
    return String(phone || "").replace(/[^\d]/g, "");
  }

  function buildWhatsAppUrl(phone, message) {
    var cleanPhone = normalizePhone(phone);
    var url = "https://wa.me/" + cleanPhone;

    if (message) {
      url += "?text=" + encodeURIComponent(message);
    }

    return url;
  }

  function serializeForm(form) {
    var data = {};
    var formData = new FormData(form);

    formData.forEach(function (value, key) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        if (!Array.isArray(data[key])) {
          data[key] = [data[key]];
        }

        data[key].push(value);
        return;
      }

      data[key] = value;
    });

    return data;
  }

  function interpolateTemplate(template, data) {
    return String(template || "").replace(/\{\{\s*([\w.-]+)\s*\}\}/g, function (_, key) {
      var value = data;
      var parts = key.split(".");

      for (var i = 0; i < parts.length; i += 1) {
        if (value == null) return "";
        value = value[parts[i]];
      }

      return value == null ? "" : String(value);
    });
  }

  function setExpanded(trigger, panel, expanded) {
    trigger.setAttribute("aria-expanded", expanded ? "true" : "false");

    if (panel) {
      panel.hidden = !expanded;
      panel.setAttribute("aria-hidden", expanded ? "false" : "true");
    }
  }

  function initAccordions(context) {
    queryAll(selectors.accordion, context).forEach(function (accordion) {
      if (initialized.has(accordion)) return;
      initialized.add(accordion);

      var allowMultiple = toBoolean(accordion.dataset.maAccordionMultiple, false);
      var items = queryAll(selectors.accordionItem, accordion);

      items.forEach(function (item, index) {
        var trigger = query(selectors.accordionTrigger, item);
        var panel = query(selectors.accordionPanel, item);
        if (!trigger || !panel) return;

        var panelId = panel.id || uid("ma-accordion-panel");
        var triggerId = trigger.id || uid("ma-accordion-trigger");
        var expanded = toBoolean(item.dataset.maAccordionOpen, index === 0 && toBoolean(accordion.dataset.maAccordionFirstOpen, false));

        panel.id = panelId;
        trigger.id = triggerId;
        trigger.setAttribute("aria-controls", panelId);
        panel.setAttribute("aria-labelledby", triggerId);
        setExpanded(trigger, panel, expanded);

        trigger.addEventListener("click", function () {
          var isOpen = trigger.getAttribute("aria-expanded") === "true";

          if (!allowMultiple && !isOpen) {
            items.forEach(function (otherItem) {
              if (otherItem === item) return;
              var otherTrigger = query(selectors.accordionTrigger, otherItem);
              var otherPanel = query(selectors.accordionPanel, otherItem);
              if (otherTrigger && otherPanel) {
                setExpanded(otherTrigger, otherPanel, false);
              }
            });
          }

          setExpanded(trigger, panel, !isOpen);
          emit(accordion, "accordion-toggle", {
            item: item,
            trigger: trigger,
            panel: panel,
            expanded: !isOpen
          });
        });
      });
    });
  }

  function activateTab(tabs, tab, focusTab) {
    var tabId = tab.getAttribute("data-ma-tab");
    var group = tab.getAttribute("data-ma-tab-group") || tabs.dataset.maTabs || "";
    var allTabs = queryAll(selectors.tab, tabs);
    var panels = queryAll(selectors.tabPanel, tabs.parentElement || doc).filter(function (panel) {
      var panelGroup = panel.getAttribute("data-ma-tab-group") || group;
      return panelGroup === group;
    });

    allTabs.forEach(function (candidate) {
      var selected = candidate === tab;
      candidate.setAttribute("aria-selected", selected ? "true" : "false");
      candidate.setAttribute("tabindex", selected ? "0" : "-1");
      candidate.classList.toggle("is-active", selected);
    });

    panels.forEach(function (panel) {
      var active = panel.getAttribute("data-ma-tab-panel") === tabId;
      panel.hidden = !active;
      panel.setAttribute("aria-hidden", active ? "false" : "true");
      panel.classList.toggle("is-active", active);
    });

    if (focusTab) {
      tab.focus({ preventScroll: true });
    }

    emit(tabs, "tab-change", {
      tab: tab,
      id: tabId,
      group: group
    });
  }

  function initTabs(context) {
    queryAll(selectors.tabs, context).forEach(function (tabs) {
      if (initialized.has(tabs)) return;
      initialized.add(tabs);

      var group = tabs.dataset.maTabs || uid("ma-tabs");
      var tabListId = tabs.id || group + "-list";
      var tabItems = queryAll(selectors.tab, tabs);

      tabs.id = tabListId;
      tabs.setAttribute("role", tabs.getAttribute("role") || "tablist");

      tabItems.forEach(function (tab, index) {
        var tabId = tab.getAttribute("data-ma-tab") || String(index);
        var panel = query('[data-ma-tab-panel="' + cssEscape(tabId) + '"]', tabs.parentElement || doc);
        var id = tab.id || group + "-tab-" + index;
        var panelId = panel && (panel.id || group + "-panel-" + index);

        tab.id = id;
        tab.setAttribute("role", tab.getAttribute("role") || "tab");
        tab.setAttribute("data-ma-tab-group", group);

        if (panel) {
          panel.id = panelId;
          panel.setAttribute("role", panel.getAttribute("role") || "tabpanel");
          panel.setAttribute("data-ma-tab-group", group);
          panel.setAttribute("aria-labelledby", id);
          tab.setAttribute("aria-controls", panelId);
        }

        tab.addEventListener("click", function () {
          activateTab(tabs, tab, false);
        });

        tab.addEventListener("keydown", function (event) {
          var currentIndex = tabItems.indexOf(tab);
          var nextIndex = currentIndex;

          if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            nextIndex = (currentIndex + 1) % tabItems.length;
          } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
            nextIndex = (currentIndex - 1 + tabItems.length) % tabItems.length;
          } else if (event.key === "Home") {
            nextIndex = 0;
          } else if (event.key === "End") {
            nextIndex = tabItems.length - 1;
          } else {
            return;
          }

          event.preventDefault();
          activateTab(tabs, tabItems[nextIndex], true);
        });
      });

      var activeTab =
        tabItems.find(function (tab) {
          return tab.getAttribute("aria-selected") === "true" || tab.classList.contains("is-active");
        }) || tabItems[0];

      if (activeTab) {
        activateTab(tabs, activeTab, false);
      }
    });
  }

  function getModal(id) {
    if (!id) return null;
    return query('[data-ma-modal="' + cssEscape(id) + '"], #' + cssEscape(id));
  }

  function hydrateDeferredModalMedia(modal) {
    if (!modal) return;

    queryAll("[data-ma-modal-srcset]", modal).forEach(function (media) {
      var srcset = media.dataset.maModalSrcset;
      var sizes = media.dataset.maModalSizes;

      if (srcset) media.setAttribute("srcset", srcset);
      if (sizes) media.setAttribute("sizes", sizes);

      media.removeAttribute("data-ma-modal-srcset");
      media.removeAttribute("data-ma-modal-sizes");
    });

    queryAll("img[data-ma-modal-src]", modal).forEach(function (image) {
      var src = image.dataset.maModalSrc;
      if (src) image.setAttribute("src", src);
      image.removeAttribute("data-ma-modal-src");
    });
  }

  function closeModal(modal, options) {
    if (!modal) return;

    var detail = options || {};

    /* MA-CORE-01 -- only the modal that currently owns the scroll lock may
       release it. closeModal() previously called unlockScroll() (and reset
       previousFocus) unconditionally, for ANY modal handed to it. A
       [data-ma-modal-close="some-other-id"] trigger, a backdrop/cancel
       event arriving for a modal that is not the active one, or a plain
       second close of an already-closed modal therefore decremented
       scrollLockDepth without a matching lockScroll(). The depth could hit
       0 while another modal was still open, unlocking page scroll behind a
       visible dialog and stealing focus back to the wrong element. The
       hide itself is still performed unconditionally -- that part is
       idempotent and harmless -- but the lock/focus bookkeeping now runs
       only for the active modal. */
    var wasActive = activeModal === modal;

    if (modal.matches("dialog") && typeof modal.close === "function" && modal.open) {
      modal.close();
    } else {
      modal.hidden = true;
      modal.setAttribute("aria-hidden", "true");
    }

    modal.classList.remove("is-open");

    if (wasActive) {
      unlockScroll();
      activeModal = null;
    }

    emit(modal, "modal-close", detail);

    if (wasActive) {
      if (previousFocus && typeof previousFocus.focus === "function") {
        previousFocus.focus({ preventScroll: true });
      }
      previousFocus = null;
    }
  }

  function openModal(modal, options) {
    if (!modal) return;

    if (activeModal === modal) {
      /* Idempotency guard: a second open of the SAME already-active modal
         (e.g. two separate triggers on one page both pointing at
         "ma-consultation-popup" -- sticky CTA, hero, CTA band, doctor
         cards, Medical Hub consult button) previously re-ran the full
         open sequence, calling lockScroll() again without a matching
         second close, permanently stranding scrollLockDepth above 0 after
         the visitor closed the modal once. Re-opening an already-open
         modal has no visible effect anyway, so this is a pure no-op with
         no behavior change for the normal single-open path or for
         switching between two different modals (unaffected, see below). */
      return;
    }

    if (activeModal && activeModal !== modal) {
      closeModal(activeModal, { reason: "switch" });
    }

    previousFocus = doc.activeElement;
    activeModal = modal;
    hydrateDeferredModalMedia(modal);
    modal.classList.add("is-open");
    modal.removeAttribute("hidden");
    modal.setAttribute("aria-hidden", "false");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("role", modal.getAttribute("role") || "dialog");

    lockScroll();

    if (modal.matches("dialog") && typeof modal.showModal === "function" && !modal.open) {
      modal.showModal();
    }

    var focusTarget =
      query("[data-ma-modal-focus]", modal) ||
      getFocusable(modal)[0] ||
      modal;

    if (!focusTarget.hasAttribute("tabindex") && focusTarget === modal) {
      focusTarget.setAttribute("tabindex", "-1");
    }

    window.setTimeout(function () {
      focusTarget.focus({ preventScroll: true });
    }, 0);

    emit(modal, "modal-open", options || {});
  }

  function trapModalFocus(event) {
    if (!activeModal || event.key !== "Tab") return;

    var focusable = getFocusable(activeModal);
    if (!focusable.length) {
      event.preventDefault();
      activeModal.focus({ preventScroll: true });
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && doc.activeElement === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && doc.activeElement === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  function initModals(context) {
    queryAll(selectors.modal, context).forEach(function (modal) {
      if (initialized.has(modal)) return;
      initialized.add(modal);

      modal.hidden = !modal.classList.contains("is-open") && !modal.open;
      modal.setAttribute("aria-hidden", modal.hidden ? "true" : "false");

      modal.addEventListener("click", function (event) {
        if (!toBoolean(modal.dataset.maModalCloseOnBackdrop, true)) return;

        var isBackdropClick =
          event.target === modal ||
          (event.target.classList && event.target.classList.contains("ma-modal__backdrop"));

        if (isBackdropClick) {
          closeModal(modal, { reason: "backdrop" });
        }
      });

      modal.addEventListener("cancel", function (event) {
        if (!toBoolean(modal.dataset.maModalCloseOnEsc, true)) {
          event.preventDefault();
          return;
        }

        event.preventDefault();
        closeModal(modal, { reason: "escape" });
      });
    });

    queryAll(selectors.modalOpen, context).forEach(function (trigger) {
      if (initialized.has(trigger)) return;
      initialized.add(trigger);

      trigger.addEventListener("click", function (event) {
        var target = trigger.getAttribute("data-ma-modal-open");
        var modal = getModal(target);
        if (!modal) return;

        event.preventDefault();
        openModal(modal, {
          trigger: trigger,
          source: "manual"
        });
      });
    });

    queryAll(selectors.modalClose, context).forEach(function (trigger) {
      if (initialized.has(trigger)) return;
      initialized.add(trigger);

      trigger.addEventListener("click", function (event) {
        var explicitTarget = trigger.getAttribute("data-ma-modal-close");
        var modal = explicitTarget ? getModal(explicitTarget) : closest(trigger, selectors.modal);
        if (!modal) return;

        event.preventDefault();
        closeModal(modal, {
          trigger: trigger,
          reason: "manual"
        });
      });
    });
  }

  function updateSlider(slider) {
    var track = query(selectors.sliderTrack, slider);
    if (!track) return;

    var items = prepareSliderItems(slider);
    if (!items.length) return;

    var previous = query(selectors.sliderPrev, slider);
    var next = query(selectors.sliderNext, slider);
    var dots = queryAll(selectors.sliderDot, slider);
    var maxScroll = track.scrollWidth - track.clientWidth;
    var current = items.reduce(function (bestIndex, item, index) {
      var distance = Math.abs(item.offsetLeft - track.scrollLeft);
      var bestDistance = Math.abs(items[bestIndex].offsetLeft - track.scrollLeft);
      return distance < bestDistance ? index : bestIndex;
    }, 0);

    if (previous) {
      previous.disabled = track.scrollLeft <= 1;
      previous.setAttribute("aria-disabled", previous.disabled ? "true" : "false");
    }

    if (next) {
      next.disabled = track.scrollLeft >= maxScroll - 1;
      next.setAttribute("aria-disabled", next.disabled ? "true" : "false");
    }

    dots.forEach(function (dot, index) {
      var active = index === current;
      dot.classList.toggle("is-active", active);
      dot.setAttribute("aria-current", active ? "true" : "false");
    });

    slider.dataset.maSliderIndex = String(current);

    emit(slider, "slider-update", {
      index: current,
      total: items.length,
      maxScroll: maxScroll
    });
  }

  function prepareSliderItems(slider) {
    var track = query(selectors.sliderTrack, slider);
    if (!track) return [];

    return Array.prototype.slice.call(track.children).filter(function (item, index) {
      if (item.nodeType !== 1) return false;

      if (!item.hasAttribute("data-ma-slider-item")) {
        item.setAttribute("data-ma-slider-item", String(index));
      }

      if (!item.hasAttribute("role")) {
        item.setAttribute("role", "listitem");
      }

      return true;
    });
  }

  function scrollSliderTo(slider, index) {
    var track = query(selectors.sliderTrack, slider);
    if (!track) return;

    var items = prepareSliderItems(slider);
    var item = items[index];
    if (!item) return;

    track.scrollTo({
      left: item.offsetLeft,
      behavior: prefersReducedMotion() ? "auto" : "smooth"
    });
  }

  function moveSlider(slider, direction) {
    var track = query(selectors.sliderTrack, slider);
    if (!track) return;

    var items = prepareSliderItems(slider);
    var current = toNumber(slider.dataset.maSliderIndex, 0);
    var next = current + direction;
    var loop = toBoolean(slider.dataset.maSliderLoop, false);

    if (loop && items.length) {
      if (next < 0) next = items.length - 1;
      if (next > items.length - 1) next = 0;
    } else {
      next = Math.max(0, Math.min(items.length - 1, next));
    }

    scrollSliderTo(slider, next);
  }

  function buildSliderDots(slider) {
    var dotsContainer = query(selectors.sliderDots, slider);
    var track = query(selectors.sliderTrack, slider);
    if (!dotsContainer || !track || dotsContainer.children.length) return;

    prepareSliderItems(slider).forEach(function (_, index) {
      var dot = doc.createElement("button");
      dot.type = "button";
      dot.className = "ma-slider__dot";
      dot.setAttribute("data-ma-slider-dot", String(index));
      dot.setAttribute("aria-label", dotsContainer.dataset.maSliderDotLabel ? interpolateTemplate(dotsContainer.dataset.maSliderDotLabel, { index: index + 1 }) : String(index + 1));
      dotsContainer.appendChild(dot);
    });
  }

  function initSliders(context) {
    queryAll(selectors.slider, context).forEach(function (slider) {
      if (initialized.has(slider)) return;
      initialized.add(slider);

      var track = query(selectors.sliderTrack, slider);
      if (!track) return;

      prepareSliderItems(slider);
      buildSliderDots(slider);

      var previous = query(selectors.sliderPrev, slider);
      var next = query(selectors.sliderNext, slider);
      var dots = queryAll(selectors.sliderDot, slider);
      var update = throttle(function () {
        updateSlider(slider);
      }, 80);

      track.setAttribute("tabindex", track.getAttribute("tabindex") || "0");

      if (previous) {
        previous.addEventListener("click", function () {
          moveSlider(slider, -1);
        });
      }

      if (next) {
        next.addEventListener("click", function () {
          moveSlider(slider, 1);
        });
      }

      dots.forEach(function (dot, index) {
        dot.addEventListener("click", function () {
          scrollSliderTo(slider, toNumber(dot.getAttribute("data-ma-slider-dot"), index));
        });
      });

      track.addEventListener("scroll", update, { passive: true });
      window.addEventListener("resize", update);
      updateSlider(slider);

      if (toBoolean(slider.dataset.maSliderAutoplay, false)) {
        var interval = Math.max(1200, toNumber(slider.dataset.maSliderAutoplayInterval, 5000));
        var autoplayTimer = null;

        var stopAutoplay = function () {
          window.clearInterval(autoplayTimer);
          autoplayTimer = null;
        };

        var startAutoplay = function () {
          if (autoplayTimer || prepareSliderItems(slider).length < 2 || prefersReducedMotion()) return;

          autoplayTimer = window.setInterval(function () {
            if (doc.hidden) return;
            moveSlider(slider, 1);
          }, interval);
        };

        slider.addEventListener("mouseenter", stopAutoplay);
        slider.addEventListener("mouseleave", startAutoplay);
        slider.addEventListener("focusin", stopAutoplay);
        slider.addEventListener("focusout", startAutoplay);
        doc.addEventListener("visibilitychange", function () {
          if (doc.hidden) {
            stopAutoplay();
          } else {
            startAutoplay();
          }
        });

        startAutoplay();
      }
    });
  }

  function initForms(context) {
    queryAll(selectors.form, context).forEach(function (form) {
      if (initialized.has(form)) return;
      initialized.add(form);

      form.addEventListener("submit", function (event) {
        var modes = toArray(form.dataset.maSubmitMode || form.dataset.maFormMode);
        var data = serializeForm(form);
        var detail = {
          form: form,
          data: data,
          modes: modes
        };

        emit(form, "form-submit", detail);

        /* Stage 2 remediation (F14): the arbitrary-webhook submit mode
           (data-ma-form-mode="webhook" + data-ma-webhook-url) was removed
           here. It let any [data-ma-form] element fetch-POST its full
           serialized field data to a URL taken from a data attribute. A
           theme-wide trace (every snippet/section with a form: contact-form,
           email-signup, gift-card-recipient-form, blog-comment-form, and
           the consultation popup) found zero elements using this mode --
           the consultation form uses Shopify's native `contact` action
           instead. Removed rather than hardened, since an unused arbitrary
           destination is a larger risk than a missing feature; the
           "whatsapp" submit mode below (client-side only, no network
           request) is unaffected. */

        if (modes.indexOf("whatsapp") !== -1 && form.dataset.maWhatsappPhone) {
          var message = interpolateTemplate(form.dataset.maWhatsappMessage || "", data);
          var url = buildWhatsAppUrl(form.dataset.maWhatsappPhone, message);

          if (toBoolean(form.dataset.maWhatsappPreventDefault, modes.length === 1)) {
            event.preventDefault();
          }

          window.open(url, form.dataset.maWhatsappTarget || "_blank", "noopener,noreferrer");
        }
      });
    });
  }

  function initScrollTo(context) {
    queryAll(selectors.scrollTo, context).forEach(function (trigger) {
      if (initialized.has(trigger)) return;
      initialized.add(trigger);

      trigger.addEventListener("click", function (event) {
        var targetSelector = trigger.getAttribute("data-ma-scroll-to");
        var target = targetSelector && query(targetSelector);
        if (!target) return;

        event.preventDefault();
        target.scrollIntoView({
          block: trigger.dataset.maScrollBlock || "start",
          inline: trigger.dataset.maScrollInline || "nearest",
          behavior: prefersReducedMotion() ? "auto" : trigger.dataset.maScrollBehavior || "smooth"
        });
      });
    });
  }

  function initTriggers(context) {
    queryAll(selectors.trigger, context).forEach(function (trigger) {
      if (initialized.has(trigger)) return;
      initialized.add(trigger);

      var type = trigger.getAttribute("data-ma-trigger");
      var target = trigger.dataset.maTriggerTarget;
      var modal = target ? getModal(target) : null;
      var onceKey = trigger.dataset.maTriggerOnceKey || "ma-trigger-fired-" + (target || uid("target")) + "-" + type;
      var once = toBoolean(trigger.dataset.maTriggerOnce, true);
      var storage = getSessionStorage();

      function fire(source) {
        if (once && storage && storage.getItem(onceKey)) return;

        if (once && storage) {
          storage.setItem(onceKey, "1");
        }

        emit(trigger, "trigger-fire", {
          source: source,
          target: target,
          modal: modal
        });

        if (modal) {
          openModal(modal, {
            trigger: trigger,
            source: source
          });
        }
      }

      if (type === "timer") {
        window.setTimeout(function () {
          fire("timer");
        }, Math.max(0, toNumber(trigger.dataset.maTriggerDelay, 5000)));
      }

      if (type === "engagement") {
        var engagementStarted = false;

        function removeEngagementListeners() {
          window.removeEventListener("pointerdown", startEngagementTimer);
          window.removeEventListener("scroll", startEngagementTimer);
          doc.removeEventListener("keydown", startEngagementTimer);
        }

        function startEngagementTimer() {
          if (engagementStarted) return;
          engagementStarted = true;
          removeEngagementListeners();
          window.setTimeout(function () {
            fire("engagement");
          }, Math.max(0, toNumber(trigger.dataset.maTriggerDelay, 5000)));
        }

        window.addEventListener("pointerdown", startEngagementTimer, { passive: true });
        window.addEventListener("scroll", startEngagementTimer, { passive: true });
        doc.addEventListener("keydown", startEngagementTimer);
      }

      if (type === "scroll") {
        var threshold = Math.min(100, Math.max(0, toNumber(trigger.dataset.maTriggerPercent, 50)));
        var onScroll = throttle(function () {
          var scrollTop = window.scrollY || doc.documentElement.scrollTop || 0;
          var height = doc.documentElement.scrollHeight - window.innerHeight;
          var percent = height <= 0 ? 100 : (scrollTop / height) * 100;

          if (percent >= threshold) {
            window.removeEventListener("scroll", onScroll);
            fire("scroll");
          }
        }, 160);

        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
      }

      if (type === "exit") {
        var exitHandler = function (event) {
          if (event.clientY > toNumber(trigger.dataset.maTriggerExitY, 24)) return;

          doc.removeEventListener("mouseleave", exitHandler);
          fire("exit");
        };

        doc.addEventListener("mouseleave", exitHandler);
      }

      if (type === "view") {
        if ("IntersectionObserver" in window) {
          var observer = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;

              observer.disconnect();
              fire("view");
            });
          }, {
            threshold: toNumber(trigger.dataset.maTriggerThreshold, 0.45)
          });

          observer.observe(trigger);
        } else {
          fire("view");
        }
      }
    });
  }

  function loadStyle(href, options) {
    var id = options && options.id ? options.id : href;
    if (!href) return Promise.reject(new Error("Missing stylesheet href"));
    if (loadedAssets.has(id)) return loadedAssets.get(id);

    var existing = query('link[href="' + cssEscape(href) + '"]');
    if (existing) {
      var existingPromise = Promise.resolve(existing);
      loadedAssets.set(id, existingPromise);
      return existingPromise;
    }

    var promise = new Promise(function (resolve, reject) {
      var link = doc.createElement("link");
      link.rel = "stylesheet";
      link.href = href;

      if (options && options.media) {
        link.media = options.media;
      }

      link.onload = function () {
        resolve(link);
      };

      link.onerror = function () {
        reject(new Error("Failed to load stylesheet: " + href));
      };

      doc.head.appendChild(link);
    });

    loadedAssets.set(id, promise);
    return promise;
  }

  function loadScript(src, options) {
    var id = options && options.id ? options.id : src;
    if (!src) return Promise.reject(new Error("Missing script src"));
    if (loadedAssets.has(id)) return loadedAssets.get(id);

    var existing = query('script[src="' + cssEscape(src) + '"]');
    if (existing) {
      var existingPromise = Promise.resolve(existing);
      loadedAssets.set(id, existingPromise);
      return existingPromise;
    }

    var promise = new Promise(function (resolve, reject) {
      var script = doc.createElement("script");
      script.src = src;
      script.async = options && Object.prototype.hasOwnProperty.call(options, "async") ? Boolean(options.async) : true;
      script.defer = options && Object.prototype.hasOwnProperty.call(options, "defer") ? Boolean(options.defer) : true;

      if (options && options.type) {
        script.type = options.type;
      }

      script.onload = function () {
        resolve(script);
      };

      script.onerror = function () {
        reject(new Error("Failed to load script: " + src));
      };

      doc.head.appendChild(script);
    });

    loadedAssets.set(id, promise);
    return promise;
  }

  function init(context) {
    var scope = context || doc;

    initAccordions(scope);
    initTabs(scope);
    initModals(scope);
    initSliders(scope);
    initForms(scope);
    initScrollTo(scope);
    initTriggers(scope);

    emit(scope === doc ? doc.body || root : scope, "ready", {
      context: scope
    });
  }

  /* Omega's injected floating WhatsApp/phone links contain only icons and do
     not expose accessible names. Keep this integration bridge deliberately
     scoped to the app's own container and only fill a missing aria-label; an
     app-provided label always wins. The short-lived observer covers the app's
     asynchronous injection without becoming a permanent page observer. */
  function labelOmegaContactLinks(context) {
    var labels = {
      whatsapp: "Contact Manmukh Ayurveda on WhatsApp",
      phone: "Call Manmukh Ayurveda"
    };

    queryAll(".cta_group_preview a[data-type]", context || doc).forEach(function (link) {
      var type = String(link.getAttribute("data-type") || "").toLowerCase();
      if (!link.getAttribute("aria-label") && labels[type]) {
        link.setAttribute("aria-label", labels[type]);
      }
    });
  }

  function initOmegaAccessibilityBridge() {
    labelOmegaContactLinks(doc);
    if (!window.MutationObserver || !doc.body) return;

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes, function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches(".cta_group_preview a[data-type]")) {
            labelOmegaContactLinks(node.parentNode || doc);
          } else if (node.querySelector) {
            labelOmegaContactLinks(node);
          }
        });
      });
    });

    observer.observe(doc.body, { childList: true, subtree: true });
    window.setTimeout(function () { observer.disconnect(); }, 10000);
  }

  doc.addEventListener("keydown", function (event) {
    if (event.key === "Escape" && activeModal && toBoolean(activeModal.dataset.maModalCloseOnEsc, true)) {
      closeModal(activeModal, { reason: "escape" });
      return;
    }

    trapModalFocus(event);
  });

  /* Stage 2 remediation (F5): centralized, privacy-safe click-to-analytics
     dispatch. Several CTAs already carry data-event/data-action attributes
     (home hero, sticky CTA, section header, doctor section, CTA band) but
     nothing ever read them -- this is the missing listener side. Publishes
     through Shopify's own documented analytics.publish() API so it reaches
     whichever Web Pixel / Customer Event subscriber is actually installed,
     instead of guessing at a specific vendor SDK. The payload is limited to
     structural metadata already present in the markup (event name, action,
     and the nearest [data-ma-component] name) -- never a label, URL, phone
     number, or field value, matching the privacy rule the WhatsApp/
     consultation flows already follow for anything health- or PII-adjacent. */
  doc.addEventListener("click", function (event) {
    var target = event.target && event.target.closest && event.target.closest("[data-event]");
    if (!target) return;

    var eventName = target.getAttribute("data-event");
    if (!eventName) return;

    try {
      if (!(window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === "function")) {
        return;
      }

      var componentEl = closest(target, "[data-ma-component]");

      window.Shopify.analytics.publish(eventName, {
        action: target.getAttribute("data-action") || undefined,
        component: componentEl ? componentEl.getAttribute("data-ma-component") : undefined
      });
    } catch (error) {
      /* Analytics must never break the click it's observing. */
    }
  });

  window[namespace] = Object.assign(window[namespace] || {}, {
    version: "1.0.0",
    selectors: selectors,
    ready: ready,
    init: init,
    query: query,
    queryAll: queryAll,
    closest: closest,
    uid: uid,
    emit: emit,
    debounce: debounce,
    throttle: throttle,
    toBoolean: toBoolean,
    toNumber: toNumber,
    toArray: toArray,
    cssEscape: cssEscape,
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,
    openModal: openModal,
    closeModal: closeModal,
    buildWhatsAppUrl: buildWhatsAppUrl,
    serializeForm: serializeForm,
    interpolateTemplate: interpolateTemplate,
    loadStyle: loadStyle,
    loadScript: loadScript,
    prefersReducedMotion: prefersReducedMotion,
    refreshSliders: function (context) {
      queryAll(selectors.slider, context || doc).forEach(updateSlider);
    }
  });

  ready(function () {
    init(doc);
    initOmegaAccessibilityBridge();
  });

  /* Theme Editor: newly-loaded/rearranged sections bring fresh DOM
     nodes that need their own initialization — existing nodes are a
     no-op thanks to the shared `initialized` bound-check already used
     inside every init* helper above (initAccordions, initModals,
     initSliders, etc.), so this is safe to call repeatedly without
     creating duplicate listeners or duplicate autoplay timers. Same
     pattern as assets/ma-3d.js and assets/ma-card-save.js. */
  doc.addEventListener("shopify:section:load", function (event) {
    init(event.target);
  });
})();
